import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { BsX } from 'react-icons/bs';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import {  addDoc,serverTimestamp } from 'firebase/firestore';


import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';

const formatAIMessage = (text) => {
  const formatted = text
    .replace(/✅\s\*\*Correct Answer\*\*:\s*/g, '<br/><br/><strong>✅ Correct Answer:</strong><br/>')
    .replace(/🧠\s\*\*Key Insight\*\*:\s*/g, '<br/><br/><strong>🧠 Key Insight:</strong><br/>')
    .replace(/📊\s\*\*Key Action\*\*:\s*/g, '<br/><br/><strong>📊 Key Action:</strong><br/>')
    .replace(/🗨️\s\*\*Answer\*\*:\s*/g, '<br/><br/><strong>🗨️ Answer:</strong><br/>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  return formatted.trim();
};


const staticAIResponses = {

  "magic-moment-crypto-q1": [
    `✅ Correct Answer: "Wait" – This is because a bounce near support with a long wick suggests buying interest, but confirmation is key before jumping in.

🧠 Key Insight:
When Ethereum prints a long lower wick near a known support level like $1,800, it shows buyers stepped in during the day. But ending the session as a red candle tells us bears still had some control. This mix of signals is common in volatile markets.

As a trader, it's tempting to “buy the dip,” especially when price touches familiar levels. But wise traders wait for confirmation—like a green candle follow-through, volume increase, or bullish reversal pattern. Acting too early could mean catching a falling knife if support breaks.

This situation teaches patience and discipline, essential traits for consistent trading success.

📊 Key Action:
If you're watching ETHUSD at support, the best move is to wait and observe the next candle. Does it confirm the bounce with bullish momentum? Or do sellers push the price lower again?

For beginners, think of this as a "test" moment: you're training your eye to spot high-probability setups. Write down what you see. Was there volume? Was the wick long compared to body? Did the next candle confirm strength?

Patience isn’t just passive—it’s a strategy. Wait for the chart to show you the story before writing your trade.`,
    `🗨️ **Answer**: A breakout is a powerful signal that the market has overcome resistance. It typically leads to a new trend direction, especially when supported by strong volume.
🧠 **Key Insight**: Breakouts with volume reflect conviction. Many traders enter trades based on these signals, causing momentum. However, caution is needed to avoid traps.
📊 **Key Action**: When identifying breakouts, confirm with volume and wait for a retest. This reduces false entry chances and improves your trade's success rate.`,
    `🗨️ **Answer**: Price breaking above a known resistance suggests bullish momentum and fresh demand.
🧠 **Key Insight**: Inexperienced traders often enter late. Understanding breakout mechanics helps build better entries with less risk.
📊 **Key Action**: Use alerts on resistance zones and review past breakout patterns. Set alerts and journal the outcome.`,
    `🗨️ **Answer**: Breakouts are popular strategies, but not all are created equal. Volume and follow-through matter.
🧠 **Key Insight**: Study the price action before and after breakouts. Avoid chasing; wait for pullbacks.
📊 **Key Action**: Backtest breakouts using platforms like TradingView. Gain confidence through paper trading these setups.`
  ],

  "magic-moment-crypto-q2": [
    `✅ Correct Answer: "Wait" – This is because while the price has broken above a key psychological level with volume, it’s important to confirm whether the breakout holds before entering.

🧠 Key Insight:
When Solana (SOLUSD) breaks above a psychological level like $125 with a volume spike, traders often get excited. However, not every breakout leads to sustained momentum. Many fakeouts lure in eager buyers, only to reverse sharply.

Volume is a great signal—it shows market interest—but the price action that follows matters more. If the breakout holds and gets follow-through, it's likely a real move. But if price retreats back below the breakout point, it could trap buyers.

As a beginner trader, you’re learning the difference between reaction and confirmation. Reacting too soon can cost you. Waiting for the next candle, volume continuation, or a retest of $125 as support gives you an edge.

📊 Key Action:
Pause before hitting buy. Instead, watch how the next session plays out. Is price staying above $125? Is volume staying strong? Does it pull back and bounce off the $125 level?

Your action plan should be:

Set an alert for price dropping back below $125.

Monitor if the level flips from resistance to support.

Only consider buying if the trend confirms itself with momentum and structure.

By learning to wait, you avoid FOMO and trade with more clarity and confidence.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
  ],

  "magic-moment-crypto-q3": [
   `✅ Correct Answer: "Sell" – This is because a bearish engulfing candle after approaching resistance often signals a potential reversal, especially when you’re already in a long trade and price is dropping.

🧠 Key Insight:
A bearish engulfing pattern happens when sellers take full control after a bullish candle, often near a resistance level. In this case, you bought AVAXUSD on March 22 and now it's showing signs of weakness. That’s a key moment for disciplined decision-making.

The lesson here is to accept when the market is not moving in your favor. Holding onto a losing trade, hoping it turns around, is one of the biggest pitfalls beginners face. Recognizing a shift in sentiment early allows you to cut losses quickly and protect capital.

It's also a reminder to set a trading plan before entering — with defined stop-loss levels, targets, and what invalidates your idea. That way, when the chart gives you a clear bearish signal, you can act without hesitation or emotion.

📊 Key Action:
Now’s the time to act like a trader, not a hopeful investor.

If AVAXUSD is showing a bearish engulfing pattern near resistance, your setup is invalidated.

Close the trade, review what you learned, and reset.

You can always re-enter later if momentum returns, but holding onto a losing position can drain both your account and confidence. Selling here helps you stay objective and sharp for the next trade.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
   ],
  "magic-moment-crypto-q4": [
    `✅ Correct Answer: "Buy" – This is because a strong breakout with bullish follow-through and high volume often confirms momentum, making it a good entry point for traders.

🧠 Key Insight:
When an asset like Natural Gas (XNGUSD) breaks out from a period of consolidation — especially after a week-long base — and follows through with a 4.27% gain and strong volume, it’s often a sign of conviction by large players.

This kind of price behavior reflects a shift in sentiment — from indecision to strength. Momentum traders love this setup because the breakout suggests a new trend leg is starting. However, it’s essential to evaluate risk-to-reward, avoid chasing after long moves without pullbacks, and know your exit plan.

In this case, the surge suggests a likely continuation — especially if it's testing March highs and pushing through.

📊 Key Action:
As a beginner trader, you could treat this scenario as a learning opportunity for swing trading:

Entry: You could enter after confirmation of the breakout with a stop below the breakout zone.

Risk: Manage your stop-loss to avoid giving back gains on a reversal.

Profit Plan: Plan your exit near the next resistance level.

This is also a good time to explore trading with momentum, learning how to time entries not at the top, but on follow-through setups with volume. Remember: don’t chase blindly — use structure, wait for confirmation, and trade the plan.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
  ],
  "magic-moment-crypto-q5": [
   `✅ Correct Answer: "Sell" – This is because repeated failures to break resistance at $5.18 and a red close on March 29 signal growing seller strength and potential reversal.

🧠 Key Insight:
When a security tests the same resistance level multiple times without success, it shows that buyers are losing momentum, and sellers are stepping in with conviction. This is particularly significant if the price closes lower with strong selling pressure after the second or third attempt, as seen in this Copper chart.

This pattern often hints at distribution — where smart money starts to unload positions at a known level. If the resistance at $5.18 holds again, and price action fails to follow through with higher highs, it can be a warning sign of a trend exhaustion or reversal.

For traders, this is an opportunity to either take profits, reduce exposure, or consider shorting — especially if confirmation comes through breakdowns or bearish volume.

📊 Key Action:
As a beginner, observe how price behaves near key levels like $5.18:

If it repeatedly rejects, ask: "What’s the market telling me?"

Red candles near resistance after multiple failures are a signal to exit or avoid new longs.

Here, you’d treat this as a moment to tighten risk — consider reducing your position or selling to protect profits. Resist FOMO or false hope that price will break next time. Let confirmation — not hope — guide your trade.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
   ],
  "magic-moment-commodities-q1": [
   `✅ Correct Answer: "Buy" – This is because Gold has broken above a key resistance level ($3,080) with a strong bullish candle and high volume, indicating potential trend continuation.

🧠 Key Insight:
When an asset breaks above a well-established resistance level, especially with high volume and strong bullish momentum, it often signals a continuation of the uptrend. In this case, Gold (XAUUSD) was already in a rally phase. On March 29, it not only broke through the $3,080 barrier—a psychological and technical level—but did so with a surge in trading volume (353.8K ticks). This volume confirms strong market participation, suggesting that institutional and retail traders alike are supporting the move. For beginners, it's important to distinguish between real breakouts and "fakeouts." Volume is a powerful filter—low-volume breakouts can quickly reverse, while high-volume ones often follow through.

📊 Key Action:
As a beginner trader, in a situation like this, you should first validate the breakout by checking the candle’s body (a full bullish candle is better than a wick) and confirming that volume is significantly above the average. Once confirmed, entering a long (buy) trade can be considered with a clear risk management plan. Place a stop-loss slightly below the breakout zone or the previous swing low to manage downside risk. Additionally, consider scaling into the position rather than going all-in—this way, you can adapt if the breakout pulls back before continuing. Always check for major news or economic data that might impact commodities like gold, especially interest rate changes or inflation reports.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
  ],
  "magic-moment-commodities-q2": [
   `✅ Correct Answer: "Short" – This is because a bearish engulfing candle forming near resistance after a weak rally often signals the beginning of a potential downtrend.

🧠 Key Insight:
A bearish engulfing candle is a classic reversal pattern where the current day's candle fully engulfs the previous day's candle and closes lower. This indicates strong selling pressure. In this scenario, WTI Crude Oil attempted to push above the key psychological level of $7,000 but was firmly rejected on March 29, closing down 1.3% and forming a bearish engulfing candle near $6,990. When such a pattern appears after a weak rally and near resistance, it reflects not only failure to break higher, but also potential exhaustion among buyers and increasing momentum from sellers.

For beginner traders, it's important to recognize that patterns like these are more powerful when they align with broader context—such as being at resistance and occurring with notable volume or momentum shift. While this doesn’t guarantee a trend reversal, it increases the probability of short-term downside.

📊 Key Action:
If you see this setup in the market, consider it a potential short opportunity, especially if supported by additional indicators like declining RSI or MACD crossover. Before entering, check the next key support level—this helps determine your target area. Place your stop-loss just above the bearish engulfing candle's high to protect against invalidation. Also, be cautious around macro events like inventory reports or geopolitical news, which can drastically affect oil prices. If you're not ready to short outright, consider paper trading or watching how price behaves the following day to build confidence in reading candlestick patterns and risk setups.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
   ],
  "magic-moment-commodities-q3": [
   `✅ Correct Answer: "Wait" – This is because a price spike without strong volume often lacks conviction and could be a false breakout or liquidity trap.

🧠 Key Insight:
In technical analysis, volume is confirmation. When price breaks a key level or surges suddenly, volume should rise to validate the move. In this case, Silver (XAGUSD) hit an intraday high of $34.13 on March 28 but closed flat—and did so on low volume. That’s a red flag. It suggests that while price attempted to push higher, there wasn't enough buyer interest to sustain the move. This could have been caused by stop runs (forcing shorts to cover), or speculative noise with no institutional follow-through.

For traders, volume + price = conviction. If one is missing, caution is warranted. Acting prematurely can result in getting trapped at the top of a failed breakout. Recognizing when not to trade is just as powerful as knowing when to act.

📊 Key Action:
A smart move here is to wait for confirmation. That means watching if Silver breaks above $34.13 again—this time with a decisive bullish candle and increasing volume. Alternatively, a close back below recent support could suggest the move was a fakeout and lead to a short setup. For beginners, learn to filter the noise. Don’t FOMO into every spike. Use tools like volume bars, RSI, or moving averages to build a case—not just react to price alone. Sometimes, doing nothing is a strong trading decision. Let the market prove itself.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
  ],
  "magic-moment-commodities-q4": [
   `✅ Correct Answer: "Buy" – This is because a strong breakout following a period of consolidation, especially with high volume and price follow-through, often signals momentum continuation.

🧠 Key Insight:
Markets often move in phases—consolidation, breakout, trend, and reversion. In this case, Natural Gas (XNGUSD) consolidated for a week before breaking out with a 4.27% gain on March 29, accompanied by strong volume. That’s a textbook signal of strength. When a market breaks recent highs with force, it typically attracts new buyers and stops out short sellers, fueling further momentum.

For traders, this is a potential swing trading setup. You’re not aiming for micro profits but instead positioning for a multi-day move. Momentum + volume + breakout from a base = a compelling signal. The key is to enter with a stop-loss below the breakout zone and let the trend do the work.

📊 Key Action:
As a beginner, this is a moment to lean into momentum—but with structure. Consider entering a small long position and managing risk using a stop-loss below the previous range. You could even scale in further if momentum builds. Watch for confirmation in subsequent candles: strong closes, rising volume, and clean trend structure.

Also, consider using a trailing stop if the move continues — this lets profits run while locking in gains. Don't rush out of good trades too early. Let the market validate your decision. Momentum trades like this can build confidence — and your trading account.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
   ],
  "magic-moment-commodities-q5": [
   `✅ Correct Answer: "Sell" – This is because multiple failed attempts to break resistance, especially when followed by a red candle and selling pressure, often signal bullish exhaustion and reversal risk.

🧠 Key Insight:
In technical analysis, repeated rejection at a key resistance level (in this case, $5.18) is a red flag. Buyers have tried to push Copper above that line twice in 3 days, but each time the market rejected the move. On March 29, a red close with visible selling confirms that supply is outweighing demand.

This is often a precursor to profit-taking or even a trend reversal. Smart traders recognize when momentum shifts and act defensively. Trying to push through resistance without volume or follow-through is like running into a wall—eventually, the market backs off.

📊 Key Action:
As a beginner, this is an opportunity to practice defensive trading. If you’re in a long trade, consider selling or at least reducing your position. This preserves capital and lets you re-enter if the breakout eventually comes with strength.

If you’re flat (no position), this is a great example of why patience pays—resistance exists for a reason. Use such scenarios to observe how price behaves at key levels. If Copper continues to reject, it may even present a shorting opportunity with proper risk control.`,
    `🗨️ **Answer**: When price breaks out above resistance, it confirms buyers are in control.
🧠 **Key Insight**: Strong breakouts shift trader psychology. The market often moves fast, leaving slow traders behind.
📊 **Key Action**: React with a plan—either enter on confirmation or wait for a pullback. Always protect capital with stops.`,
    `🗨️ **Answer**: Momentum-driven moves usually start with a breakout.
🧠 **Key Insight**: Traders often use indicators like MACD or RSI to confirm strength.
📊 **Key Action**: Learn to overlay these indicators and measure volume spikes. That gives you an edge during breakouts.`,
    `🗨️ **Answer**: Breakouts can trigger institutional entries too.
🧠 **Key Insight**: Large volume may represent institutional interest. This can validate the breakout's strength.
📊 **Key Action**: Study how volume spikes correlate with trend continuation. Keep a log of past trade examples.`
   ],

   "onboarding-skill-test-mc-q1": [
   `✅ Correct Answer: "Wait" – This is because Ethereum is retesting a key support level, and it’s important to wait for confirmation before taking action to avoid false signals.

🧠 Key Insight:
Support levels act as psychological floors where buyers previously stepped in. When price returns to these levels — like ETHUSD at $1,800 — it presents a potential bounce opportunity, but also carries the risk of a breakdown. Beginners often rush to "buy the dip," but experienced traders wait for confirmation. A support test alone doesn’t guarantee a reversal; you need evidence such as a bullish candlestick pattern, increased buying volume, or a clear rejection wick to signal buyer strength.

Jumping in too early can lead to "catching a falling knife" if support fails. By waiting for follow-through, like a green candle close or bounce on volume, traders protect themselves from false setups. It’s also critical to observe the broader trend. Is ETHUSD in an uptrend pulling back? Or is it in a downtrend testing support from below? Context matters.

📊 Key Action:
As a beginner trader, treat this setup as a learning moment. Don't trade immediately. Instead:

Watch the next 1–2 candles. Do you see a bullish engulfing candle? A hammer wick showing rejection? Or does price keep dropping?

Check volume. Rising volume with bullish price action near support = potential bounce. Weak or declining volume = wait.

Set alerts just above and below $1,800. Let the chart come to you.`,
    ``,
    ``
   ],
   "onboarding-skill-test-mc-q2": [
   `✅ Correct Answer: "Hold" – This is because sharp news-driven drops often result in short-term overreactions, and holding can allow time for price stabilization or recovery.

🧠 Key Insight:
In financial markets, sudden news (like political announcements) can trigger immediate price shocks. These reactions are often emotional and short-lived, especially when there's uncertainty about the long-term impact. In this scenario, Apple (AAPL) fell sharply after Trump’s import tax policy announcement. However, unless this fundamentally changes Apple’s long-term outlook, the selloff may be temporary.

Smart traders and investors distinguish between noise and narrative. The market may initially panic, but large institutions often reassess calmly and may buy the dip. Holding through the noise gives your position a chance to recover — especially if you're already in with a small size and the broader trend remains intact.

It's also a lesson in position sizing and emotional control. If your position is manageable, holding through volatility may be better than reacting impulsively to news.

📊 Key Action:
As a beginner, here’s how to approach this situation:

Zoom Out: Look at the 1-week or 1-month chart. Is this drop just a blip in a larger uptrend?

Check Volume: If the drop happened on low volume, it may not reflect broad investor conviction.

Evaluate the News: Is the policy likely to affect Apple’s earnings meaningfully? Or is it just short-term sentiment?

Rather than sell into fear, consider tightening your stop-loss or reviewing your thesis. If you believe in Apple long-term, a sharp drop might be noise, not signal. Hold, observe, and learn. News fades, but trading discipline lasts.`,
    ``,
    ``
   ],
   "onboarding-skill-test-mc-q3": [
   `✅ Correct Answer: "Buy" – This is because gold approaching a major psychological level like $3,000 during inflationary conditions suggests growing demand for safe-haven assets.

🧠 Key Insight:
Gold has historically been seen as a store of value during inflationary periods. When inflation rises, currency purchasing power declines — pushing investors to seek protection in hard assets like gold. In this scenario, Gold (XAUUSD) nearing $3,000 reflects growing concern about inflation and the market's shift toward defensive assets.

The key idea here is hedging against uncertainty. When traditional assets become volatile or threatened by macroeconomic forces, gold often holds or even increases in value. This psychological threshold ($3,000) also plays into investor behavior — round numbers tend to attract attention and trigger momentum.

As a beginner, it's crucial to recognize the narrative behind price action — it's not just charts, it's what they represent. Rising inflation = rising gold demand = price surge. Don’t just follow price — understand what drives it.

📊 Key Action:
In this moment, a beginner trader should:

Confirm the Trend: Look at the weekly chart. Is gold trending upward with momentum and volume?

Watch for Breakout: A strong daily close above $3,000 could trigger further buying from institutional players.

Set Risk Parameters: Place a stop-loss below the previous support zone ($2,950 or similar) to manage downside.

You’re not just buying gold — you're buying protection against uncertainty. This trade isn’t just technical, it's psychological and macroeconomic. Start with small size, monitor inflation data, and use this as a real-world lesson on how global events move markets.`,
    ``,
    ``
   ],
   "onboarding-skill-test-mc-q4": [
   `✅ Correct Answer: "Short" – This is because a break below a key support level like $215 with rising volume often signals a bearish shift and opens the door for further downside.

🧠 Key Insight:
Support levels act like a psychological floor where buyers previously stepped in. When that floor breaks—especially on high volume—it indicates that the demand at that level has been overwhelmed by selling pressure. In Tesla's case, $215 was a recent support zone. When price decisively breaks below such a level, it often triggers stop-losses, algorithmic trades, and panic selling, all of which can accelerate the drop.

This type of setup is often a moment of decision for traders: either exit existing long positions or consider short strategies if you believe the trend has reversed. Volume matters here—it confirms that the break isn’t just noise but likely has conviction behind it.

As a trader, you’re not just reacting to price; you’re interpreting behavior. A support break tells you the bulls have lost their grip—for now.

📊 Key Action:
As a beginner trader, here's how to handle this situation:

Do Not Buy the Dip Blindly: Wait to see if the level is reclaimed. Otherwise, you're fighting momentum.

Consider a Short: If your platform allows shorting, this could be a learning opportunity. But start with paper trading to understand how short setups behave.

Look for Confirmation: Watch if the next candles continue lower or if $215 gets retested as resistance (a classic “support turned resistance” scenario).

Most importantly, manage risk. Set a clear stop-loss (e.g., just above $215) and have a target (like the next support zone). This trade is all about momentum and confirmation—ride the trend, but don't chase blindly.`,
    ``,
    ``
   ],
   "onboarding-skill-test-mc-q5": [
    `✅ Correct Answer: "Wait" – This is because policy-driven moves often create temporary volatility, and price nearing resistance requires confirmation before making a trade.

🧠 Key Insight:
When currency pairs like EURUSD move sharply due to central bank policy changes, it can result in emotional, short-term volatility rather than a sustainable trend. In this case, EURUSD is nearing a known resistance level (1.18) following a policy shift on March 24, 2025. While that spike might excite traders, resistance zones are where price often stalls or reverses.

Jumping in too quickly—especially right before a resistance test—can result in buying the top or falling into a false breakout. It's wiser to observe how the market reacts to that level. Does it break through cleanly on volume and close above it? Or does it get rejected and start to pull back?

Waiting helps you avoid being a reactive trader and instead become a strategic decision-maker, responding only when the market confirms the direction.

📊 Key Action:
Here’s how to play this as a beginner:

Set Alerts at 1.18: Instead of acting, track how price behaves at this resistance level.

Look for Confirmation: Watch for candle closes above 1.18 with strong follow-through (e.g. bullish engulfing candle, volume spike).

Avoid Trading on News Alone: Central bank announcements can cause knee-jerk reactions. Let the market digest the news first.

If EURUSD breaks above and holds, a bullish trend may begin. But if it fails to break through and prints a reversal pattern, it could be a signal to fade the move or stay on the sidelines. Waiting is not weakness—it’s patience with a purpose.`,
     ``,
     ``
    ]
};

const LearnWithAIWithLiveChart = ({
  show,
  onClose,
  question,
  isCorrect,
  selectedAnswer,
  instrument,
  charttype,
  timeframe,
  intent,
  chartContextPrompt,
  practicesessionid = '',
}) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const chatEndRef = useRef(null);
  const chatSessionIdRef = useRef(null);
  const MAX_MESSAGES = 2;



  const auth = getAuth();
  const db = getFirestore();

  const [collapsedMessageIndex, setCollapsedMessageIndex] = useState(null);



const [isScrolledUp, setIsScrolledUp] = useState(false);

const handleScroll = (e) => {
  const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
  setIsScrolledUp(!bottom);
};


  useEffect(() => {
    const fetchChatHistory = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const sessionId = chatSessionIdRef.current || uuidv4();
      chatSessionIdRef.current = sessionId;

      const q = query(
        collection(db, 'user_learn_with_ai_interactions'),
        where('userid', '==', user.uid),
        where('chatsessionid', '==', sessionId),
        orderBy('messageid')
      );

      const snapshot = await getDocs(q);
      const previousMessages = snapshot.docs.flatMap((doc) => {
        const data = doc.data();
        return [
          { sender: 'user', text: data.usermessage },
          { sender: 'ai', text: data.airesponse },
        ];
      });

      const intro = {
        sender: 'ai',
        text: isCorrect
          ? '🎉 You got it right! Want to go deeper? Ask me anything about this chart.'
          : '📉 Ask me. Ready to help!',
      };

      const totalAIResponses = previousMessages.filter((m) => m.sender === 'ai').length;

      if (totalAIResponses < MAX_MESSAGES) {
        setUserInput(
          isCorrect
            ? 'Help me understand and learn more'
            : 'Help me understand why my answer was incorrect'
        );
      }

      setMessages([intro, ...previousMessages]);
      setMessageCount(totalAIResponses);
    };

    if (show) {
      fetchChatHistory();
    }
  }, [show, isCorrect]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  const handleSendMessage = async () => {
    if (!userInput.trim()) return;
  
    const userMessage = userInput.trim();
    const userId = auth.currentUser?.uid;
    const chatSessionId = chatSessionIdRef.current || uuidv4();
    chatSessionIdRef.current = chatSessionId;

    const chartContext = chartContextPrompt || 'No context';
const selectedAnswerNum = selectedAnswer?.toString() || '0';

if (!userId || !question?.questionid || !userMessage) {
  console.error('🚫 Missing required data. Aborting API call.');
  return;
}
  
    const baseMessages = [...messages, { sender: 'user', text: userMessage }];
    setMessages([...baseMessages, { sender: 'ai', text: 'I\'m thinking...' }]);
    setUserInput('');
    setLoading(true);
  
    const messageId = messageCount + 1;
  
    try {


      // For debugging
      // console.log('📦 Sending payload:', {
      //   userid: userId,
      //   chatsessionid: chatSessionId,
      //   practicesessionid,
      //   questionId: question?.questionid,
      //   questionText: question?.questionname,
      //   userMessage,
      //   selectedAnswer,
      //   correctAnswer: question?.correctanswer,
      //   iscorrect: selectedAnswer?.toString() === question?.correctanswer,
      //   chartContext: chartContextPrompt,
      //   instrument,
      //   charttype,
      //   timeframe,
      //   intent,
      //   messageCount,
      // });




      const response = await axios.post(
        'https://mcp-server-fintorro.onrender.com/agenticAiChat',
        {
          userid: userId,
          chatsessionid: chatSessionId,
          practicesessionid,
          questionId: question.questionid,
          questionText: question.questionname,
          userMessage,
          selectedAnswer: selectedAnswer?.toString(),
          correctAnswer: question.correctanswer,
          iscorrect: selectedAnswer?.toString() === question.correctanswer,
          chartContext: chartContextPrompt,
          instrument,
          charttype,
          timeframe,
          intent,
          messageCount,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-aimodel': 'gemini',
          },
        }
      );
  
      const aiReply = response.data?.reply || '⚠️ I couldn’t generate a helpful response.';
      const updatedMessages = [...baseMessages, { sender: 'ai', text: aiReply }];
      setMessages(updatedMessages);
      setCollapsedMessageIndex(updatedMessages.length - 1);
      setMessageCount((prev) => prev + 1);
    } catch (err) {
      console.error('❌ AI Error - using fallback:', err);
  
      const staticReplies = staticAIResponses[question.questionid] || [];
      const fallbackText = staticReplies.length === 0
        ? '⚠️ AI is currently unavailable and no pro tip is available.'
        : (messageCount === 0 ? staticReplies[0] : staticReplies[Math.floor(Math.random() * (staticReplies.length - 1)) + 1]);
  
      const fallbackMessage = { sender: 'ai', text: fallbackText };
      const updatedMessages = [...baseMessages, fallbackMessage];
  
      setMessages(updatedMessages);
      setCollapsedMessageIndex(updatedMessages.length - 1);
      setMessageCount((prev) => prev + 1);
  
      try {
        await addDoc(collection(db, 'user_aiagent_learn_with_ai_interactions'), {
          userid: userId,
          chatsessionid: chatSessionId,
          practicesessionid,
          questionid: question.questionid,
          questiontext: question.questionname,
          selectedAnswer: selectedAnswer?.toString(),
          correctanswer: question.correctanswer,
          iscorrect: selectedAnswer?.toString() === question.correctanswer,
          modelused: 'static-fallback',
          messageid: messageId,
          usermessage: userMessage,
          airesponse: fallbackText,
          timestamp: serverTimestamp(),
          debuginfo: {
            status: err?.response?.status || null,
            data: err?.response?.data || null,
            headers: err?.response?.headers
  ? {
      'content-type': err.response.headers['content-type'],
      'content-length': err.response.headers['content-length'],
    }
  : null,
          },
        });
      } catch (firestoreError) {
        console.error('❌ Firestore fallback logging error:', firestoreError);
      }
    } finally {
      setLoading(false);
  
      if (messageCount + 1 === MAX_MESSAGES) {
        setMessages(baseMessages);
        const upgradeText = '🚀 You’ve hit the free limit. Upgrade to FinTorro Pro to unlock unlimited AI learning!';
        setMessages((prev) => [...prev, { sender: 'ai', text: upgradeText }]);
  
        try {
          await addDoc(collection(db, 'user_aiagent_learn_with_ai_interactions'), {
            userid: userId,
            chatsessionid: chatSessionId,
            practicesessionid,
            questionid: question.questionid,
            questiontext: question.questionname,
            selectedAnswer: selectedAnswer?.toString(),
            correctanswer: question.correctanswer,
            iscorrect: selectedAnswer?.toString() === question.correctanswer,
            modelused: 'system-upgrade-limit',
            messageid: messageId + 1,
            usermessage: '[system] upgrade prompt',
            airesponse: upgradeText,
            timestamp: serverTimestamp(),
          });
        } catch (err) {
          console.error('❌ Firestore Upgrade Save Error:', err);
        }
      }
    }
  };



  const handleUpgradeClick = () => {
    window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank", "noopener,noreferrer");
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header
        className="d-flex justify-content-between align-items-center border-0"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <h5 className="fw-bold mb-0" style={{ color: '#FF7C24' }}>Learn with AI</h5>
        <Button
          variant="link"
          onClick={onClose}
          aria-label="Close"
          style={{ fontSize: '1.5rem', color: '#232830' }}
        >
          <BsX size={24} />
        </Button>
      </Modal.Header>

      {/* <Modal.Body
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          backgroundColor: '#FFFFFF',
          padding: '1rem',
        }}
      > */}
<Modal.Body
  onScroll={handleScroll}
  style={{
    maxHeight: '400px',
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    position: 'relative',
  }}
>




        <div className="d-flex flex-column" style={{ gap: '18px', paddingBottom: '20px' }}>

          {/* {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                backgroundColor: msg.sender === 'user' ? '#007BFF' : '#232830',
                color: '#FFFFFF',
                padding: '12px 16px',
                borderRadius: '16px',
                maxWidth: '80%',
                fontSize: '16px',
                lineHeight: '1.4',
                textAlign: msg.sender === 'user' ? 'right' : 'left',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {msg.sender === 'ai'
                ? <div dangerouslySetInnerHTML={{ __html: formatAIMessage(msg.text) }} />
                : msg.text}
            </div>
          ))} */}

{messages.map((msg, idx) => {
  const isCollapsed = idx === collapsedMessageIndex;
  const formattedText = formatAIMessage(msg.text);

  if (msg.sender === 'ai' && isCollapsed) {
    const preview = formattedText.split('<br/>').slice(0, 6).join('<br/>'); // First 3 lines

    return (
      <div
        key={idx}
        style={{
          alignSelf: 'flex-start',
          backgroundColor: '#232830',
          color: '#FFFFFF',
          padding: '12px 16px',
          borderRadius: '16px',
          maxWidth: '80%',
          fontSize: '16px',
          position: 'relative',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: preview + '<br/>...' }} />
        <div
          onClick={() => setCollapsedMessageIndex(null)}
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '12px',
            bottom: '12px',
            cursor: 'pointer',
            fontSize: '24px',
            backgroundColor: '#FF7C24',
            color: '#fff',
            padding: '4px 10px',
            borderRadius: '20px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          ⬇️
        </div>
      </div>
    );
  }

  // Normal full message
  return (
    <div
      key={idx}
      style={{
        alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
        backgroundColor: msg.sender === 'user' ? '#007BFF' : '#232830',
        color: '#FFFFFF',
        padding: '12px 16px',
        borderRadius: '16px',
        maxWidth: '80%',
        fontSize: '16px',
        textAlign: msg.sender === 'user' ? 'right' : 'left',
      }}
    >
      {msg.sender === 'ai'
        ? <div dangerouslySetInnerHTML={{ __html: formatAIMessage(msg.text) }} />
        : msg.text}
    </div>
  );
})}



{loading && (
  <div
    style={{
      alignSelf: 'flex-start',
      backgroundColor: '#f0f0f0',
      color: '#232830',
      padding: '10px 16px',
      borderRadius: '16px',
      fontSize: '16px',
      fontStyle: 'italic',
      maxWidth: '60%',
    }}
  >
    I'm thinking...
  </div>
)}

          <div ref={chatEndRef}></div>


           {/* ⬇️ Scroll to Bottom Icon */}
          {isScrolledUp && (
            <div
              onClick={() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                position: 'absolute',
                bottom: '12px',
                right: '16px',
                backgroundColor: '#FF7C24',
                color: '#fff',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 10,
                fontSize: '18px',
              }}
              title="Scroll to bottom"
            >
              ⬇️
            </div>
          )}
       
        </div>

        
      </Modal.Body>

    


      <Modal.Footer
        className="border-0"
        style={{
          flexDirection: 'column',
          gap: '10px',
          backgroundColor: '#232830',
          width: '100%',
          padding: '20px',
        }}


      >
        {messageCount < MAX_MESSAGES ? (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              width: '100%',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Form.Control
              type="text"
              placeholder="Ask a question..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              style={{
                flex: 1,
                minHeight: '50px',
                backgroundColor: '#FFFFFF',
                color: '#232830',
                borderRadius: '12px',
                border: '1px solid #CED4DA',
                fontSize: '16px',
                padding: '0.75rem',
              }}
              disabled={loading}
            />
            <Button
              onClick={handleSendMessage}
              disabled={loading || !userInput.trim()}
              style={{
                minWidth: '100px',
                minHeight: '50px',
                backgroundColor: '#FF7C24',
                borderColor: '#FF7C24',
                color: '#FFFFFF',
                fontWeight: 'bold',
                borderRadius: '12px',
                fontSize: '16px',
                transform: loading ? 'scale(0.95)' : 'scale(1)',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {loading ? '...' : 'Send'}
            </Button>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              padding: '20px',
              borderRadius: '16px',
              width: '100%',
              color: '#232830',
              textAlign: 'center',
            }}
          >
            <p style={{ fontWeight: 'bold', fontSize: '17px' }}>Upgrade to FinTorro Pro and get:</p>
            <ul className="list-unstyled" style={{ fontSize: '15px', lineHeight: '1.6' }}>
              <li>• Unlimited Lessons & Practice</li>
              <li>• Learn with AI</li>
              <li>• 10,000+ Interactive Lessons</li>
              <li>• Crypto, Forex, Stocks & More</li>
              <li>• Join Challenges & Earn Rewards</li>
            </ul>
            <Button
              onClick={handleUpgradeClick}
              style={{
                marginTop: '10px',
                backgroundColor: '#FF7C24',
                borderColor: '#FF7C24',
                fontWeight: 'bold',
                borderRadius: '12px',
                padding: '10px 20px',
              }}
            >
              Upgrade to Pro
            </Button>
          </div>
        )}
      </Modal.Footer>

 <p style={{
  fontSize: '13px',
  textAlign: 'center',
  color: '#6c757d',
  padding: '10px 20px',
  marginTop: '8px',
}}>
  🤖 AI can make mistakes. Please use with discretion.
</p>
    </Modal>
  );
};

export default LearnWithAIWithLiveChart;




// commented 24th March 1608 production working copy


// import React, { useState, useRef, useEffect } from 'react';
// import { Modal, Button, Form, Spinner } from 'react-bootstrap';
// import styled from 'styled-components';
// import axios from 'axios';
// import { v4 as uuidv4 } from 'uuid';
// import { getAuth } from 'firebase/auth';
// import {
//   getFirestore,
//   collection,
//   getDocs,
//   query,
//   where,
//   orderBy,
// } from 'firebase/firestore';

// // Styled Components
// const MessageContainer = styled.div`
//   padding: 10px;
//   margin-bottom: 10px;
//   border-radius: 8px;
//   max-width: 80%;
//   align-self: ${(props) => (props.sender === 'user' ? 'flex-end' : 'flex-start')};
//   background-color: ${(props) => (props.sender === 'user' ? '#007bff' : '#ffffff')};
//   color: ${(props) => (props.sender === 'user' ? '#ffffff' : '#007bff')};
// `;

// const UpgradeContainer = styled.div`
//   background-color: rgba(0, 0, 255, 0.2);
//   padding: 20px;
//   margin-top: 20px;
//   border-radius: 10px;
//   color: white;
// `;

// const UpgradeButton = styled(Button)`
//   background-color: #ff7c24;
//   border-color: #ff7c24;
//   font-weight: bold;
//   margin-top: 10px;
// `;

// const formatAIMessage = (text) => {
//   const formatted = text
//     .replace(/---/g, '')
//     .replace(/(✅\s\*\*Correct Answer\*\*:)/g, '<br /><br />$1')
//     .replace(/(🧠\s\*\*Key Insight\*\*:)/g, '<br /><br />$1')
//     .replace(/(📊\s\*\*Key Action\*\*:)/g, '<br /><br />$1')
//     .replace(/(🗨️\s\*\*Answer\*\*:)/g, '<br /><br />$1')
//     .replace(/\n{2,}/g, '<br /><br />')
//     .replace(/\n/g, '<br />');
//   return formatted.trim();
// };

// const LearnWithAIWithLiveChart = ({
//   show,
//   onClose,
//   question,
//   isCorrect,
//   selectedAnswer, // e.g., 1, 2, 3...
//   instrument,
//   charttype,
//   timeframe,
//   intent,
//   chartContextPrompt,
//   practicesessionid = '',
// }) => {
//   const [messages, setMessages] = useState([]);
//   const [userInput, setUserInput] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [messageCount, setMessageCount] = useState(0);
//   const chatEndRef = useRef(null);
//   const chatSessionIdRef = useRef(null); // 🔁 Persist across renders

//   const auth = getAuth();
//   const db = getFirestore();
//   const MAX_MESSAGES = 3;

//   // ✅ Fetch previous messages or set up new chat
//   useEffect(() => {
//     const fetchChatHistory = async () => {
//       const user = auth.currentUser;
//       if (!user) return;
  
//       const sessionId = chatSessionIdRef.current || uuidv4();
//       chatSessionIdRef.current = sessionId;
  
//       const q = query(
//         collection(db, 'user_learn_with_ai_interactions'),
//         where('userid', '==', user.uid),
//         where('chatsessionid', '==', sessionId),
//         orderBy('messageid')
//       );
  
//       const snapshot = await getDocs(q);
//       const previousMessages = snapshot.docs.flatMap((doc) => {
//         const data = doc.data();
//         return [
//           { sender: 'user', text: data.usermessage },
//           { sender: 'ai', text: data.airesponse },
//         ];
//       });
  
//       const intro = {
//         sender: 'ai',
//         text: isCorrect
//           ? '🎉 You got it right! Want to go deeper? Ask me anything about this chart.'
//           : '📉 Not quite! Want to understand why? ',
//       };
  
//       const totalAIResponses = previousMessages.filter((m) => m.sender === 'ai').length;
  
//       // ✅ Set prefilled user input if under limit
//       if (totalAIResponses < MAX_MESSAGES) {
//         setUserInput(
//           isCorrect
//             ? 'Help me understand and learn more'
//             : 'Help me understand why my answer was incorrect'
//         );
//       }
  
//       setMessages([intro, ...previousMessages]);
//       setMessageCount(totalAIResponses);
//     };
  
//     if (show) {
//       fetchChatHistory();
//     }
//   }, [show, isCorrect]);
  

//   useEffect(() => {
//     chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
//   }, [messages]);

//   const handleSendMessage = async () => {
//     if (!userInput.trim()) return;

//     const userMessage = userInput.trim();
//     const newMessages = [...messages, { sender: 'user', text: userMessage }];
//     setMessages(newMessages);
//     setUserInput('');
//     setLoading(true);

//     try {
//       const response = await axios.post(
//       'http://localhost:5001/tradingfunda-4376a/us-central1/api/agenticAiChat',
//       {

//       const response = await axios.post(
//         'https://us-central1-tradingfunda-4376a.cloudfunctions.net/api/agenticAiChat',
//         {
//           userid: auth.currentUser?.uid,
//           chatsessionid: chatSessionIdRef.current,
//           practicesessionid,
//           questionId: question.questionid,
//           userMessage,
//           questionText: question.questionname,
//           selectedAnswer,
//           correctAnswer: question.correctanswer,
//           iscorrect: selectedAnswer?.toString() === question.correctanswer,
//           chartContext: chartContextPrompt,
//           instrument,
//           charttype,
//           timeframe,
//           intent,
//           messageCount,
//         }
//       );

//       const aiReply = response.data?.reply || 'Sorry, I couldn’t generate a helpful response.';
//       setMessages([...newMessages, { sender: 'ai', text: aiReply }]);
//       setMessageCount((prev) => prev + 1);
//     } catch (err) {
//       console.error('❌ Error getting AI reply:', err);
//       setMessages([
//         ...newMessages,
//         { sender: 'ai', text: '⚠️ AI is currently unavailable. Try again in a few minutes' },
//       ]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Modal show={show} onHide={onClose} centered size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>Learn with AI</Modal.Title>
//       </Modal.Header>
//       <Modal.Body style={{ backgroundColor: '#232830', color: '#ffffff' }}>
//         <div style={{ display: 'flex', flexDirection: 'column' }}>
//           {messages.map((msg, i) => (
//             <MessageContainer key={i} sender={msg.sender}>
//               {msg.sender === 'ai'
//                 ? <div dangerouslySetInnerHTML={{ __html: formatAIMessage(msg.text) }} />
//                 : msg.text}
//             </MessageContainer>
//           ))}
//           <div ref={chatEndRef} />
//         </div>

//         {messageCount >= MAX_MESSAGES ? (
//           <UpgradeContainer>
//             <p style={{ fontWeight: 'bold', fontSize: '17px' }}>Upgrade to FinTorro Pro and get:</p>
//             <ul className="list-unstyled" style={{ fontSize: '15px', lineHeight: '1.6' }}>
//               <li>• Unlimited Lessons & Practice</li>
//               <li>• Learn with AI</li>
//               <li>• 10,000+ Interactive Lessons</li>
//               <li>• Crypto, Forex, Stocks & More</li>
//               <li>• Join Challenges & Earn Rewards</li>
//             </ul>
//             <UpgradeButton onClick={() => window.open('https://buy.stripe.com/eVa29y1Ig8zsgFycMZ')}>
//               Upgrade to Pro
//             </UpgradeButton>
//           </UpgradeContainer>
//         ) : (
//           <>
//             <Form.Group controlId="userMessage" className="mt-3">
//               <Form.Control
//                 type="text"
//                 placeholder="Ask me a question..."
//                 value={userInput}
//                 onChange={(e) => setUserInput(e.target.value)}
//                 disabled={loading}
//               />
//             </Form.Group>
//             <Button
//               className="mt-2"
//               variant="primary"
//               onClick={handleSendMessage}
//               disabled={loading || !userInput.trim()}
//             >
//               {loading ? <Spinner animation="border" size="sm" /> : 'Send'}
//             </Button>
//           </>
//         )}
//       </Modal.Body>
//     </Modal>
//   );
// };

// export default LearnWithAIWithLiveChart;








// import React, { useState, useRef, useEffect } from 'react';
// import { Modal, Button, Form } from 'react-bootstrap';
// import { BsX } from 'react-icons/bs';

// const LearnWithAILiveChart = ({ show, onClose, question, isCorrect, aiResponseSet }) => {
//   const [messages, setMessages] = useState([]);
//   const [userMessage, setUserMessage] = useState(
//     isCorrect
//       ? `Help me understand more about ${question?.questionname}`
//       : 'Help me understand why my answer was incorrect'
//   );
//   const [userMessageCount, setUserMessageCount] = useState(0);
//   const chatEndRef = useRef(null);

//   useEffect(() => {
//     if (chatEndRef.current) {
//       chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
//     }
//   }, [messages]);

//   // const handleSendMessage = () => {
//   //   if (!userMessage.trim()) return;

//   //   setMessages((prev) => [...prev, { sender: 'user', text: userMessage }]);
//   //   setUserMessage('');
//   //   setUserMessageCount((count) => count + 1);

//   //   setTimeout(() => {
//   //     const responseList = aiResponseSet[question?.questionid] || [];
//   //     const randomResponse =
//   //       responseList.length > 0
//   //         ? responseList[Math.floor(Math.random() * responseList.length)]
//   //         : "You've hit the AI limit. Upgrade to continue.";

//   //     setMessages((prev) => [...prev, { sender: 'ai', text: randomResponse }]);
//   //   }, 800);
//   // };

//   const handleSendMessage = async () => {
//     if (!userMessage.trim()) return;
  
//     setMessages((prev) => [...prev, { sender: 'user', text: userMessage }]);
//     setUserMessage('');
//     setUserMessageCount((count) => count + 1);

  
//     try {

//       const response = await fetch('http://localhost:5001/tradingfunda-4376a/us-central1/api/agenticAiChat', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//           questionId: question?.questionid,
//           questionText: question?.questionname,
//           userMessage: userMessage,
//           selectedAnswer: question?.userAnswer,
//           correctAnswer: question?.correctAnswer,
//           chartName: question?.chartName || "BTCUSD",
//           chartContext: question?.chartContext || "Live BTCUSD chart and output of technical analysis"
//         })
//       });


      
      
  
//       const data = await response.json();
  
//       setMessages((prev) => [...prev, { sender: 'ai', text: data.reply || "No response from AI." }]);
//     } catch (error) {
//       setMessages((prev) => [...prev, { sender: 'ai', text: '⚠️ Failed to get AI response.' }]);
//       console.error("AI Error:", error);
//     }
//   };
  

//   const handleUpgradeClick = () => {
//     window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank", "noopener,noreferrer");
//   };

//   return (
//     <Modal show={show} onHide={onClose} size="lg" centered>
//       <Modal.Header className="d-flex justify-content-between align-items-center border-0" style={{ backgroundColor: '#FFFFFF' }}>
//         <h5 className="fw-bold mb-0" style={{ color: '#FF7C24' }}>Learn with AI</h5>
//         <Button variant="link" onClick={onClose} style={{ fontSize: '1.5rem', color: '#232830' }}>
//           <BsX size={24} />
//         </Button>
//       </Modal.Header>

//       <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#FFFFFF', padding: '1rem' }}>
//         <div className="d-flex flex-column gap-3">
//           {messages.map((msg, idx) => (
//             <div
//               key={idx}
//               style={{
//                 alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//                 backgroundColor: msg.sender === 'user' ? '#007BFF' : '#232830',
//                 color: '#FFFFFF',
//                 padding: '12px 16px',
//                 borderRadius: '16px',
//                 maxWidth: '80%',
//                 fontSize: '16px',
//                 lineHeight: '1.4',
//                 textAlign: msg.sender === 'user' ? 'right' : 'left',
//                 border: msg.sender === 'user' ? 'none' : '1px solid #007BFF'
//               }}
//             >
//               {msg.text}
//             </div>
//           ))}
//           <div ref={chatEndRef}></div>
//         </div>
//       </Modal.Body>

//       <Modal.Footer className="border-0" style={{ flexDirection: 'column', gap: '10px', backgroundColor: '#232830', padding: '20px' }}>
//         {userMessageCount < 3 ? (
//           <div style={{ display: 'flex', gap: '10px', width: '100%', flexWrap: 'wrap', alignItems: 'center' }}>
//             <Form.Control
//               type="text"
//               placeholder="Ask a question..."
//               value={userMessage}
//               onChange={(e) => setUserMessage(e.target.value)}
//               style={{ flex: 1, minHeight: '50px', backgroundColor: '#FFFFFF', color: '#232830', borderRadius: '12px', border: '1px solid #CED4DA', fontSize: '16px', padding: '0.75rem' }}
//             />
//             <Button
//               onClick={handleSendMessage}
//               disabled={!userMessage.trim()}
//               style={{ minWidth: '100px', minHeight: '50px', backgroundColor: '#FF7C24', borderColor: '#FF7C24', color: '#FFFFFF', fontWeight: 'bold', borderRadius: '12px', fontSize: '16px' }}
//             >
//               Send
//             </Button>
//           </div>
//         ) : (
//           <div style={{ backgroundColor: '#FFFFFF', padding: '20px', borderRadius: '16px', width: '100%', color: '#232830', textAlign: 'center' }}>
//             <p style={{ fontWeight: 'bold', fontSize: '17px' }}>Upgrade to FinTorro Pro and get:</p>
//             <ul className="list-unstyled" style={{ fontSize: '15px', lineHeight: '1.6' }}>
//               <li>• Unlimited Lessons & Practice</li>
//               <li>• Learn with AI</li>
//               <li>• 10,000+ Interactive Lessons</li>
//               <li>• Crypto, Forex, Stocks & More</li>
//               <li>• Join Challenges & Earn Rewards</li>
//             </ul>
//             <Button
//               onClick={handleUpgradeClick}
//               style={{ marginTop: '10px', backgroundColor: '#FF7C24', borderColor: '#FF7C24', fontWeight: 'bold', borderRadius: '12px', padding: '10px 20px' }}
//             >
//               Upgrade to Pro
//             </Button>
//           </div>
//         )}
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default LearnWithAILiveChart;






// import React, { useState, useRef, useEffect } from 'react';
// import { Modal, Button, Form } from 'react-bootstrap';
// import { BsX } from 'react-icons/bs';
// import aiResponses from '../../data/onboarding-ai-responses-live-chart';

// const LearnWithAIWithLiveChart = ({ show, onClose, question, isCorrect }) => {
//   const [messages, setMessages] = useState([]);
//   const [userMessage, setUserMessage] = useState(
//     isCorrect
//       ? `Help me understand more about ${question?.questionname}`
//       : 'Help me understand why my answer was incorrect'
//   );
//   const [userMessageCount, setUserMessageCount] = useState(0);
//   const chatEndRef = useRef(null);

//   useEffect(() => {
//     if (chatEndRef.current) {
//       chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
//     }
//   }, [messages]);

//   const handleSendMessage = () => {
//     if (!userMessage.trim()) return;

//     setMessages((prev) => [...prev, { sender: 'user', text: userMessage }]);
//     setUserMessage('');
//     setUserMessageCount((count) => count + 1);

//     setTimeout(() => {
//       const responseList = aiResponses[question?.questionid] || [];
//       const randomResponse =
//         responseList.length > 0
//           ? responseList[Math.floor(Math.random() * responseList.length)]
//           : "You've hit the AI limit. Upgrade to continue.";

//       setMessages((prev) => [
//         ...prev,
//         { sender: 'ai', text: randomResponse }
//       ]);
//     }, 800);
//   };

//   const handleUpgradeClick = () => {
//     window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank", "noopener,noreferrer");
//   };

//   return (
//     <Modal show={show} onHide={onClose} size="lg" centered>
//       <Modal.Header
//         className="d-flex justify-content-between align-items-center border-0"
//         style={{ backgroundColor: '#FFFFFF' }}
//       >
//         <h5 className="fw-bold mb-0" style={{ color: '#FF7C24' }}>
//           Learn with AI
//         </h5>
//         <Button
//           variant="link"
//           onClick={onClose}
//           aria-label="Close"
//           style={{ fontSize: '1.5rem', color: '#232830' }}
//         >
//           <BsX size={24} />
//         </Button>
//       </Modal.Header>

//       <Modal.Body
//         style={{
//           maxHeight: '400px',
//           overflowY: 'auto',
//           backgroundColor: '#FFFFFF',
//           padding: '1rem',
//         }}
//       >
//         <div className="d-flex flex-column gap-3">
//           {messages.map((msg, idx) => (
//             <div
//               key={idx}
//               style={{
//                 alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//                 backgroundColor: msg.sender === 'user' ? '#007BFF' : '#232830',
//                 color: '#FFFFFF',
//                 padding: '12px 16px',
//                 borderRadius: '16px',
//                 maxWidth: '80%',
//                 fontSize: '16px',
//                 lineHeight: '1.4',
//                 textAlign: msg.sender === 'user' ? 'right' : 'left',
//               }}
//             >
//               {msg.text}
//             </div>
//           ))}
//           <div ref={chatEndRef}></div>
//         </div>
//       </Modal.Body>

//       <Modal.Footer
//         className="border-0"
//         style={{
//           flexDirection: 'column',
//           gap: '10px',
//           backgroundColor: '#232830',
//           width: '100%',
//           padding: '20px',
//         }}
//       >
//         {userMessageCount < 3 ? (
//           <div
//             style={{
//               display: 'flex',
//               gap: '10px',
//               width: '100%',
//               flexWrap: 'wrap',
//               alignItems: 'center',
//             }}
//           >
//             <Form.Control
//               type="text"
//               placeholder="Ask a question..."
//               value={userMessage}
//               onChange={(e) => setUserMessage(e.target.value)}
//               style={{
//                 flex: 1,
//                 minHeight: '50px',
//                 backgroundColor: '#FFFFFF',
//                 color: '#232830',
//                 borderRadius: '12px',
//                 border: '1px solid #CED4DA',
//                 fontSize: '16px',
//                 padding: '0.75rem',
//               }}
//             />
//             <Button
//               onClick={handleSendMessage}
//               disabled={!userMessage.trim()}
//               style={{
//                 minWidth: '100px',
//                 minHeight: '50px',
//                 backgroundColor: '#FF7C24',
//                 borderColor: '#FF7C24',
//                 color: '#FFFFFF',
//                 fontWeight: 'bold',
//                 borderRadius: '12px',
//                 fontSize: '16px',
//               }}
//             >
//               Send
//             </Button>
//           </div>
//         ) : (
//           <div
//             style={{
//               backgroundColor: '#FFFFFF',
//               padding: '20px',
//               borderRadius: '16px',
//               width: '100%',
//               color: '#232830',
//               textAlign: 'center',
//             }}
//           >
//             <p style={{ fontWeight: 'bold', fontSize: '17px' }}>Upgrade to FinTorro Pro and get:</p>
//             <ul className="list-unstyled" style={{ fontSize: '15px', lineHeight: '1.6' }}>
//               <li>• Unlimited Lessons & Practice</li>
//               <li>• Learn with AI</li>
//               <li>• 10,000+ Interactive Lessons</li>
//               <li>• Crypto, Forex, Stocks & More</li>
//               <li>• Join Challenges & Earn Rewards</li>
//             </ul>
//             <Button
//               onClick={handleUpgradeClick}
//               style={{
//                 marginTop: '10px',
//                 backgroundColor: '#FF7C24',
//                 borderColor: '#FF7C24',
//                 fontWeight: 'bold',
//                 borderRadius: '12px',
//                 padding: '10px 20px',
//               }}
//             >
//               Upgrade to Pro
//             </Button>
//           </div>
//         )}
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default LearnWithAIWithLiveChart;